import React from "react";
import { TextInput } from "web-ui/juicy";
import { LegacyButton as Button, LegacyToggle as Toggle } from "web-ui/legacy";

import type { TtsDataResponse, TtsRequestParameters } from "../api/duolingoTts";
import { getTtsData } from "../api/duolingoTts";
import styles from "../styles/Query.module.scss";
import { UrlParameterNames } from "../util/urlParameters";

import { HelpButton } from "./HelpButton";
import { LangPicker } from "./LangPicker";
import { VoicePicker } from "./VoicePicker";

const SEARCH_LITERAL = "search";

export interface QueryResults {
  data: TtsRequestParameters[];
  initialIndex?: number;
}

export const Query = ({
  isSearchAllowed,
  onBlockedSubmit,
  onResult,
  onError,
  onSubmit,
}: {
  isSearchAllowed: boolean;
  onBlockedSubmit: () => void;
  onResult: (results: QueryResults) => void;
  onError: (error: string) => void;
  onSubmit: () => void;
}) => {
  const [voice, setVoice] = React.useState("JuniorEn");
  const [search, setSearch] = React.useState("Hello");

  const [selectedLanguage, setSelectedLanguage] = React.useState("en");
  const [isOnlyNormalSpeed, setIsOnlyNormalSpeed] = React.useState(true);

  const [wasSearchPreviouslyBlocked, setWasSearchPreviouslyBlocked] =
    React.useState(false);

  const putSearchInUrl = React.useCallback(
    (
      languageId: string,
      onlySearchNormalSpeed: boolean,
      textToRequest: string,
      voiceToRequest: string,
    ) => {
      const parameters = new URLSearchParams(window.location.search);

      addParameterOrRemoveBlankFromUrl(
        parameters,
        UrlParameterNames.language,
        languageId,
      );
      addParameterOrRemoveBlankFromUrl(
        parameters,
        UrlParameterNames.text,
        textToRequest,
      );
      addParameterOrRemoveBlankFromUrl(
        parameters,
        UrlParameterNames.voice,
        voiceToRequest,
      );

      const isSearchByVoiceOrText = languageId && voiceToRequest;
      if (isSearchByVoiceOrText) {
        // Speed parameter makes no sense when searching by hash
        addParameterOrRemoveBlankFromUrl(
          parameters,
          UrlParameterNames.onlySearchNormalSpeed,
          onlySearchNormalSpeed,
        );
      }

      const newURL = `${window.location.pathname}?${parameters}`;
      window.history.replaceState(null, "", newURL);
    },
    [],
  );

  const addParameterOrRemoveBlankFromUrl = (
    parameters: URLSearchParams,
    key: Parameters<typeof parameters.set>[0],
    value: Parameters<typeof encodeURIComponent>[0],
  ) => {
    // When the value is falsy, remove the parameter from the URL
    // unless it's a boolean in which case we might still need that
    // value depending on the default, so it's better to keep it in.
    if (value || typeof value === "boolean") {
      parameters.set(key, encodeURIComponent(value));
    } else {
      parameters.delete(key);
    }
  };

  const requestTTSData = React.useCallback(
    (
      languageId: string,
      onlySearchNormalSpeed: boolean,
      textToRequest: string,
      ttsHashToRequest: string,
      voiceToRequest: string,
    ) => {
      onSubmit();
      putSearchInUrl(
        languageId,
        onlySearchNormalSpeed,
        textToRequest,
        voiceToRequest,
      );
      (async () => {
        try {
          const searchByVoice = languageId && voiceToRequest;
          const ttsDataResponse: TtsDataResponse = await getTtsData(
            // API prefers to search by hash if all is provided,
            // so we only send the hash if we're searching by hash
            searchByVoice ? [] : [ttsHashToRequest],
            voiceToRequest,
            textToRequest,
            onlySearchNormalSpeed,
          );
          const responseItems = ttsDataResponse.tts_request_parameters_items;
          if (responseItems) {
            const indexOfHash = responseItems.findIndex(
              item => item.tts_hash === ttsHashToRequest,
            );

            const couldNotFindSpecifiedHashInSearch =
              ttsHashToRequest && indexOfHash === -1;
            if (searchByVoice && couldNotFindSpecifiedHashInSearch) {
              requestTTSData("", false, "", ttsHashToRequest, "");
              return;
            }

            onResult({
              data: responseItems,
              initialIndex: indexOfHash === -1 ? undefined : indexOfHash,
            });
          }
          // error must be of type "any" or "unknown", but eslint doesn't allow either
          // eslint-disable-next-line
        } catch (error: any) {
          onError(error.toString());
        }
      })();
    },
    [onError, onResult, onSubmit, putSearchInUrl],
  );

  const handleClick = React.useCallback(() => {
    if (!isSearchAllowed) {
      setWasSearchPreviouslyBlocked(true);
      onBlockedSubmit();
      return;
    }
    let queryText = "";
    let queryVoice = "";
    let hash = "";
    let queryLanguage = "";

    if (/[a-f0-9A-F]{32}/.test(search)) {
      hash = search;
    } else {
      if (voice.length === 0) {
        onError("Voice is required when searching by text!");
        return;
      }

      queryLanguage = selectedLanguage;
      queryText = search;
      queryVoice = voice;
    }

    requestTTSData(
      queryLanguage,
      isOnlyNormalSpeed,
      queryText,
      hash,
      queryVoice,
    );
  }, [
    isSearchAllowed,
    isOnlyNormalSpeed,
    onError,
    onBlockedSubmit,
    search,
    selectedLanguage,
    voice,
    requestTTSData,
  ]);

  const removeTtsHashFromUrl = () => {
    const parameters = new URLSearchParams(window.location.search);
    parameters.delete(UrlParameterNames.ttsHash);
    const newURL = `${window.location.pathname}?${parameters}`;
    window.history.replaceState(null, "", newURL);
  };

  React.useEffect(() => {
    if (isSearchAllowed && wasSearchPreviouslyBlocked) {
      // Since if a search is done and the hash isn't found, we prefer the hash,
      // we need to remove it from the URL so that the user can search by text
      removeTtsHashFromUrl();
      setWasSearchPreviouslyBlocked(false);
      handleClick();
    }
  }, [isSearchAllowed, handleClick, wasSearchPreviouslyBlocked]);

  React.useEffect(() => {
    const parameters = new URLSearchParams(window.location.search);
    const ttsHashParam = parameters.get(UrlParameterNames.ttsHash) ?? "";
    const voiceParam = decodeURIComponent(
      parameters.get(UrlParameterNames.voice) ?? "",
    );
    const textParam = decodeURIComponent(
      parameters.get(UrlParameterNames.text) ?? "",
    );
    const languageParam = decodeURIComponent(
      parameters.get(UrlParameterNames.language) ?? "",
    );
    const onlySearchNormalSpeedParam =
      parameters.get(UrlParameterNames.onlySearchNormalSpeed)?.toLowerCase() ===
        "true" ?? false;

    const hasValidSearchParams = voiceParam && languageParam;
    const hasTtsHashParam = ttsHashParam.length > 0;

    if (hasValidSearchParams) {
      setVoice(voiceParam);
      setSelectedLanguage(languageParam);
      setSearch(textParam);
      setIsOnlyNormalSpeed(onlySearchNormalSpeedParam);
      requestTTSData(
        languageParam,
        onlySearchNormalSpeedParam,
        textParam,
        ttsHashParam,
        voiceParam,
      );
    } else if (hasTtsHashParam) {
      setSearch(ttsHashParam);
      requestTTSData("", false, "", ttsHashParam, "");
    }
  }, [requestTTSData]);
  const searchInstructions =
    "You can search for TTS by the keywords and voice, voice only, or with a TTS hash. When searching by keywords or voice, you can limit your search to exclude slowed down TTS.\n\n" +
    "Once you get results, you can choose from them, play the audio (and visemes, if available), see details about the TTS, and adjust phoneme timings (if available).\n\n" +
    "Need more control over your keyword search? You can use some special characters to help make your search more fine-grained. " +
    '+ acts as AND, | acts as OR, * acts as a wildcard, "" wraps several terms into a phrase, () indicate precedence, ~ sets a fuzziness when used after a term or slop after a phrase, and - negates a term.';
  return (
    <div className={styles["row-wrap"]}>
      <HelpButton instructions={searchInstructions} />
      <TextInput
        className={styles["textbox"]}
        onChange={e => setSearch(e.target.value)}
        onKeyPress={e => (e.key === "Enter" ? handleClick() : null)}
        placeholder="Text ('tu oso bebe cerveza') or hash ('000001c6035224a21ca1d1c95a6b1697')"
        type="search"
        value={search}
      />
      <LangPicker
        onChange={(language: string) => {
          setSelectedLanguage(language);
          // Since voices are language dependent,
          // we reset the voice name as well
          setVoice("");
        }}
        selectedLanguage={selectedLanguage}
      />
      <VoicePicker
        onChange={(selectedVoice: string) => {
          setVoice(selectedVoice);
        }}
        selectedLanguage={selectedLanguage}
        selectedVoice={voice}
      />
      <Toggle
        checked={isOnlyNormalSpeed}
        className={styles["toggle"]}
        onChange={e => setIsOnlyNormalSpeed(e.target.checked)}
        text="Ignore slow speech"
        type="checkbox"
      />
      <Button
        color="macaw"
        onClick={handleClick}
        submitting={false}
        type="submit"
        variant="solid"
      >
        {SEARCH_LITERAL}
      </Button>
    </div>
  );
};
